import { createContext, useContext, type ReactNode } from 'react';

/**
 * Who is signed in, as the screens need to know it.
 *
 * Still called "host" auth: inside the Angular host the profile comes down
 * from the host, and standalone the app fills the same context itself. The
 * screens do not care which, which is the point of the context.
 */

/**
 * The `dashboard` object stored on the Auth0 user, as the host hands it down.
 *
 * Declared structurally here rather than imported from the dashboard feature:
 * shared-app sits below the apps and cannot reach into one. `remote-entry.ts`
 * spells the same shape out inline for the same reason.
 */
export interface HostDashboardSettings {
  widgets?: string[];
  demo_mode?: boolean;
}

export interface HostAuthUser {
  sub?: string;
  name?: string;
  email?: string;
  /** Auth0 avatar URL, shown on the user settings screen. */
  picture?: string;
  /**
   * The uuid of the distributor this user belongs to, from the Auth0
   * `company` claim. The company settings screen loads the distributor by it;
   * the numeric id domain records use is only known once that has resolved.
   */
  companyUuid?: string;
  /**
   * The Auth0 `dashboard` claim's demo flag, which hides costs and gross
   * profit from the estimator so a quote can be shown to a customer.
   *
   * Only a seed: the quote settings dialog toggles it for the session and
   * nothing writes it back, which is how the Angular screen behaved too.
   */
  demoMode?: boolean;
  /**
   * `user_metadata.dashboard` as it stands on the Auth0 user, passed straight
   * through so the dashboard can lay its panels out.
   */
  dashboard?: HostDashboardSettings;
  /**
   * The Auth0 connections this account can sign in through, from the
   * `identities` claim. Empty where the claim is absent, which is every
   * environment whose tenant does not set it.
   */
  linkedConnections?: string[];
  roles: string[];
}

export interface HostAuth {
  user: HostAuthUser | null;
  isAuthenticated: boolean;
  getAccessToken: () => Promise<string>;
  /**
   * Tells the host the signed-in user renamed themselves. The host keeps its
   * own copy of the profile — the menu bar reads the name from it — and has no
   * way of knowing this screen changed it, so the rename would otherwise only
   * show up after a reload.
   */
  onUserNameChanged?: (name: string) => void;
  /**
   * Tells the host the dashboard layout changed, so a host holding its own
   * copy of the profile can keep up.
   */
  onDashboardChange?: (dashboard: HostDashboardSettings) => void;
}

const HostAuthContext = createContext<HostAuth>({
  user: null,
  isAuthenticated: false,
  getAccessToken: () => Promise.reject(new Error('No auth provider')),
});

export function HostAuthProvider({ auth, children }: { auth: HostAuth; children?: ReactNode }) {
  return <HostAuthContext.Provider value={auth}>{children}</HostAuthContext.Provider>;
}

export function useHostAuth() {
  return useContext(HostAuthContext);
}

export function useRoles(): string[] {
  const { user } = useContext(HostAuthContext);
  return user?.roles ?? [];
}

export function useHasRole(role: string): boolean {
  const roles = useRoles();
  return roles.includes(role);
}

export function useHasAllRoles(required: string[]): boolean {
  const roles = useRoles();
  return required.every((r) => roles.includes(r));
}

export function useHasAnyRole(required: string[]): boolean {
  const roles = useRoles();
  return required.some((r) => roles.includes(r));
}
